import React from "react";
import { motion } from "framer-motion";
import SectionHeader from "./SectionHeader";
import { researchProjects } from "../mock";
import { Activity } from "lucide-react";

const STATUS_ACCENT = {
  Active: "#10b981",
  "In Development": "#FFB74D",
  Deployed: "#00BCD4",
};

const ActiveInitiativesSection = () => {
  return (
    <section
      id="initiatives"
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#FAEDE7] relative overflow-hidden"
      data-testid="active-initiatives-section"
    >
      <div className="relative max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Active Initiatives"
          title="Current"
          titleAccent="Research Projects"
          subtitle="Our multidisciplinary team is working on breakthrough projects that push the boundaries of emotional AI understanding and application."
          index={3}
          className="mb-12 sm:mb-16"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {researchProjects.map((project, index) => {
            const accent = STATUS_ACCENT[project.status] || "#888";
            return (
              <motion.article
                key={project.id}
                data-testid={`project-card-${project.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100 p-7 sm:p-8 flex flex-col min-h-[240px]"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-4 -top-10 text-[180px] font-extralight leading-none select-none"
                  style={{ color: accent, opacity: 0.06 }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>

                <div className="relative flex items-center justify-between mb-5">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.25em] rounded-sm"
                    style={{
                      background: `${accent}1A`,
                      color: accent,
                      border: `1px solid ${accent}44`,
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: accent }}
                    />
                    {project.status}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-400">
                    Project / 0{index + 1}
                  </span>
                </div>

                <h3 className="relative text-2xl sm:text-[26px] font-light text-gray-900 tracking-tight leading-[1.1]">
                  {project.title}
                </h3>

                <p className="relative mt-4 text-sm text-gray-600 font-light leading-relaxed flex-1">
                  {project.description}
                </p>

                <div className="relative mt-6 pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.25em]"
                    style={{ color: accent }}
                  >
                    <Activity className="inline w-3 h-3 mr-1.5 -mt-0.5" />
                    In progress
                  </span>
                  <span
                    className="h-px w-10 transition-all duration-500 group-hover:w-20"
                    style={{ background: accent }}
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ActiveInitiativesSection;
